/**
 * MCP Read Tools
 *
 * Tools for reading and inspecting IDF model data without modifications.
 */

import type { MCPTool } from '../types';

/**
 * Get model summary and statistics
 */
const getModelSummary: MCPTool = {
  name: 'get_model_summary',
  description: `Get a summary of the current EnergyPlus model including version, object counts, zones, HVAC systems, and key statistics. Use this as the first tool to understand the model structure.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get objects by class name
 */
const getObjectsByClass: MCPTool = {
  name: 'get_objects_by_class',
  description: `Get all objects of a specific class type (e.g., "Zone", "Material", "Schedule:Compact"). Returns object IDs, names, and field values.`,
  inputSchema: {
    type: 'object',
    properties: {
      className: {
        type: 'string',
        description: 'The EnergyPlus object class name (e.g., "Zone", "BuildingSurface:Detailed")',
      },
      limit: {
        type: 'number',
        description: 'Maximum number of objects to return (default: 50)',
        default: 50,
      },
    },
    required: ['className'],
  },
};

/**
 * Get a specific object by ID
 */
const getObjectById: MCPTool = {
  name: 'get_object_by_id',
  description: `Get detailed information about a specific object including all field values, field names from the schema, units, and validation status.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The unique object ID',
      },
    },
    required: ['objectId'],
  },
};

/**
 * Get object by name
 */
const getObjectByName: MCPTool = {
  name: 'get_object_by_name',
  description: `Find an object by its name field. Useful when you know the object name but not its ID. Returns the first matching object.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectName: {
        type: 'string',
        description: 'The name of the object to find',
      },
      className: {
        type: 'string',
        description: 'Optional: filter by class name for faster lookup',
      },
    },
    required: ['objectName'],
  },
};

/**
 * Get field value
 */
const getFieldValue: MCPTool = {
  name: 'get_field_value',
  description: `Get the value of a specific field in an object. Also returns field metadata from the schema including type, units, valid range, and choices.`,
  inputSchema: {
    type: 'object',
    properties: {
      objectId: {
        type: 'string',
        description: 'The object ID',
      },
      fieldIndex: {
        type: 'number',
        description: 'The 0-based field index',
      },
    },
    required: ['objectId', 'fieldIndex'],
  },
};

/**
 * Get schema for object type
 */
const getObjectSchema: MCPTool = {
  name: 'get_object_schema',
  description: `Get the IDD schema definition for an object type including all field definitions, types, units, ranges, choices, and documentation. Use this to understand valid values for fields.`,
  inputSchema: {
    type: 'object',
    properties: {
      className: {
        type: 'string',
        description: 'The EnergyPlus object class name',
      },
    },
    required: ['className'],
  },
};

/**
 * List all object classes in the model
 */
const listObjectClasses: MCPTool = {
  name: 'list_object_classes',
  description: `List all unique object class names present in the current model with their counts. Useful for understanding what types of objects exist.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get zones list
 */
const getZones: MCPTool = {
  name: 'get_zones',
  description: `Get a list of all thermal zones in the model with their basic properties like floor area, volume, and ceiling height if available.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get surfaces for a zone
 */
const getZoneSurfaces: MCPTool = {
  name: 'get_zone_surfaces',
  description: `Get all surfaces (walls, floors, roofs, windows, doors) associated with a specific zone.`,
  inputSchema: {
    type: 'object',
    properties: {
      zoneName: {
        type: 'string',
        description: 'The name of the zone',
      },
    },
    required: ['zoneName'],
  },
};

/**
 * Get HVAC system overview
 */
const getHvacOverview: MCPTool = {
  name: 'get_hvac_overview',
  description: `Get an overview of the HVAC systems in the model including air loops, plant loops, zone equipment, and their connections.`,
  inputSchema: {
    type: 'object',
    properties: {},
  },
};

/**
 * Get all available object types from schema
 */
const listSchemaObjectTypes: MCPTool = {
  name: 'list_schema_object_types',
  description: `List all available EnergyPlus object types from the IDD schema, organized by group. Use this to discover what objects can be added to the model.`,
  inputSchema: {
    type: 'object',
    properties: {
      group: {
        type: 'string',
        description: 'Optional: filter by group name (e.g., "Simulation Parameters", "Zone HVAC Equipment Connections")',
      },
      searchQuery: {
        type: 'string',
        description: 'Optional: search for object types by name',
      },
    },
  },
};

export const readTools: MCPTool[] = [
  getModelSummary,
  getObjectsByClass,
  getObjectById,
  getObjectByName,
  getFieldValue,
  getObjectSchema,
  listObjectClasses,
  getZones,
  getZoneSurfaces,
  getHvacOverview,
  listSchemaObjectTypes,
];
